import React, { useEffect, useCallback, useRef } from "react";
import { format } from "date-fns";
import {
  Typography,
  makeStyles,
  createStyles,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroller";

import { get曜日 } from "../../helper/JapanDate";
import { fetchNote, NOTE_MAX_SIZE } from "../../helper/Api";
import { errors, getApiError } from "../../helper/Error";
import ErrorDialog from "../common/ErrorDialog";
import { useStateSafe } from "../../helper/customHook";

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      margin: theme.spacing(2),
    },
    name: {
      textAlign: "left",
      width: "100%",
      maxWidth: "600px",
      marginBottom: theme.spacing(4),
      fontSize: 21,
    },
    note: {
      textAlign: "left",
      width: "100%",
      maxWidth: "600px",
    },
    day: {
      marginTop: theme.spacing(3),
    },
    paper: {
      padding: theme.spacing(2),
      whiteSpace: "pre-line", // \nで改行用
    },
    text: {
      fontSize: 20,
      background:
        "linear-gradient(90deg, rgba(237, 119, 128, 0) 0%, rgba(237, 119, 128, 0) 50%, #fff 0%, #fff 100%), linear-gradient(180deg, rgba(100, 100, 100, 0) 0%, rgba(100, 100, 100, 0) 97.5%, #646464 100%)",
      backgroundSize: "8px 100%,100% 2em",
      lineHeight: "2em",
    },
    subheader: {
      fontSize: 20,
    },
    title: {
      fontSize: 27,
    },
    cardPadding: {
      padding: 0,
    },
    nothing: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      whiteSpace: "pre-line",
      fontSize: 22,
      marginTop: theme.spacing(22),
    },
    initialloader: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(22),
    },
    moreLoader: {
      display: "flex",
      justifyContent: "center",
    },
  })
);

export default function Note() {
  const location = useLocation();
  const { serviceId } = location.state ?? 0;

  const classes = useStyles();

  const [data, setData] = useStateSafe([]);
  const [isInitialLoading, setIsInitialLoading] = useStateSafe(true);
  const [hasMore, setHasMore] = useStateSafe(true);
  const serviceIdRef = useRef(serviceId);

  const errorRef = useRef({});
  const [isOpenError, setIsOpenError] = useStateSafe(false);
  const handleCloseError = useCallback(() => {
    setIsOpenError(false);
  }, [setIsOpenError]);

  useEffect(() => {
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(async () => {
    await fetchNote(serviceIdRef.current)
      .then((res) => {
        const isError = !!res.data.error.length;
        const hasResult = !!res.data.result.length;
        const isMore = res.data.result.length === NOTE_MAX_SIZE;

        if (isError) {
          throw res.data.error;
        }

        if (hasResult) {
          // 配列最後のサービスIDを保存
          serviceIdRef.current = res.data.result.slice(-1)[0].service_id;
          setData([...data, ...res.data.result]);
        }

        if (!isMore) {
          setHasMore(false);
        }
      })
      .catch((err) => {
        errorRef.current = err.code
          ? getApiError(err)
          : errors.APP_CONNECTION_FAILED;
        setIsOpenError(true);
      });

    if (isInitialLoading) {
      setIsInitialLoading(false);
    }
  }, [
    data,
    isInitialLoading,
    setData,
    setHasMore,
    setIsOpenError,
    setIsInitialLoading,
  ]);

  return (
    <>
      {isInitialLoading ? (
        <div className={classes.initialloader}>
          <CircularProgress />
        </div>
      ) : data.length === 0 ? (
        <Typography color="textSecondary" className={classes.nothing}>
          {"過去の特記・連絡事項\nはありません"}
        </Typography>
      ) : (
        <InfiniteScroll
          initialLoad={false}
          loadMore={loadMore}
          hasMore={hasMore}
          loader={
            <div className={classes.moreLoader} key={0}>
              <CircularProgress />
            </div>
          }
          useWindow={false}
        >
          <div className={classes.root}>
            <div className={classes.note}>
              {data.map((day, dayIdx) => (
                <div className={classes.day} key={dayIdx}>
                  <Card
                    elevation={3}
                    className={classes.paper}
                    classes={{ root: classes.cardPadding }}
                  >
                    <CardHeader
                      title={format(
                        new Date(day.date),
                        `M月d日(${get曜日(new Date(day.date))})`
                      )}
                      subheader={`${RegExp(/\d+:\d+/).exec(
                        day.begintime
                      )}-${RegExp(/\d+:\d+/).exec(day.endtime)} ${
                        day.servicename
                      }\n${day.kaigosha}`}
                      classes={{
                        subheader: classes.subheader,
                        title: classes.title,
                      }}
                    />

                    <CardContent>
                      <Typography
                        className={classNames(classes.text, "canSelect")}
                      >
                        {day.note}
                      </Typography>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </InfiniteScroll>
      )}

      <ErrorDialog
        open={isOpenError}
        onClose={handleCloseError}
        error={errorRef.current}
      />
    </>
  );
}
